import { Color3 as BABYLON_Color3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.color.js';
import { MultiMaterial as BABYLON_MultiMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/multiMaterial.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { ViewerMaterial } from './material/ViewerMaterial.js';
import { LightSystem } from './light-manager/light-system/LightSystem.js';
export function mark_material_rendered(material, rendered = true) {
    material.metadata = material.metadata || {};
    material.metadata.rendered = rendered;
}
export function is_material_rendered(material) {
    return material.metadata && material.metadata.rendered;
}
export class Material {
    constructor(material_id, texture_manager, freezing_manager, material) {
        this.freezing_manager = freezing_manager;
        this._material = null;
        this._multi_material = null;
        this.is_default_created_material = false;
        this._material_url = null;
        this._custom_base_color = null;
        this._original_base_color = new BABYLON_Color3(1.0, 1.0, 1.0);
        this._custom_emissive_color = null;
        this._original_emissive_color = BABYLON_Color3.Black();
        this._custom_emissive_intensity = -1;
        this._original_emissive_intensity = 0.5;
        this._custom_roughness = null;
        this._original_roughness = 1.0;
        this._custom_metallic = null;
        this._original_metallic = 1.0;
        this._custom_base_color_texture = null;
        this._original_base_color_texture = null;
        this._custom_normal_texture = null;
        this._original_normal_texture = null;
        this._custom_occlusion_roughness_metallic_texture = null;
        this._original_occlusion_roughness_metallic_texture = null;
        this._custom_lightmap_texture = null;
        this._original_lightmap_texture = null;
        this.ready = Promise.resolve();
        this.material_id = material_id;
        this._texture_manager = texture_manager;
        if (material instanceof ViewerMaterial) {
            this._material = material;
            this._material.maxSimultaneousLights = LightSystem.BABYLON_MATERIAL_MAXIMUM_LIGHT_COUNT;
        }
        else if (material instanceof BABYLON_MultiMaterial) {
            this._multi_material = material;
            this._multi_material.subMaterials.forEach(sub_material => {
                if (sub_material instanceof ViewerMaterial) {
                    sub_material.maxSimultaneousLights = LightSystem.BABYLON_MATERIAL_MAXIMUM_LIGHT_COUNT;
                }
            });
        }
    }
    get multi_material() {
        return this._multi_material;
    }
    set multi_material(material) {
        if (material === this._multi_material) {
            return;
        }
        if (this._material) {
            this._material.dispose();
            this._material = null;
        }
        if (this._multi_material) {
            this._multi_material.dispose();
        }
        this._multi_material = material;
        this.set_dirty();
    }
    get material() {
        return this._material;
    }
    set material(material) {
        if (material === this._material) {
            return;
        }
        if (this._material) {
            this._material.dispose();
        }
        if (this._multi_material) {
            this._multi_material.dispose();
            this._multi_material = null;
        }
        this._material = material;
        if (material) {
            // Store original material values
            this._original_metallic = material.metallic === null ? 1.0 : material.metallic;
            this._original_roughness = material.roughness === null ? 1.0 : material.roughness;
            this._original_base_color = new BABYLON_Color3(Math.pow(material.albedoColor.r, 1 / 2.2), Math.pow(material.albedoColor.g, 1 / 2.2), Math.pow(material.albedoColor.b, 1 / 2.2));
            this._original_emissive_color = material.emissiveColor.clone();
            this._original_emissive_intensity = material.emissiveIntensity;
            if (material.albedoTexture) {
                this._original_base_color_texture = this.init_texture(material.albedoTexture);
            }
            if (material.bumpTexture) {
                this._original_normal_texture = this.init_texture(material.bumpTexture);
            }
            if (material.metallicTexture) {
                this._original_occlusion_roughness_metallic_texture = this.init_texture(material.metallicTexture);
            }
            if (material.lightmapTexture) {
                this._original_lightmap_texture = this.init_texture(material.lightmapTexture);
            }
            material.useLightmapAsShadowmap = true; // Lightmaps in viewer are always multiplicative
            // Apply custom values, if set
            if (this._custom_base_color !== null) {
                material.albedoColor.set(Math.pow(this._custom_base_color.r, 2.2), Math.pow(this._custom_base_color.g, 2.2), Math.pow(this._custom_base_color.b, 2.2));
            }
            if (this._custom_metallic !== null) {
                material.metallic = this._custom_metallic;
            }
            if (this._custom_roughness !== null) {
                material.roughness = this._custom_roughness;
            }
            if (this._custom_base_color_texture !== null) {
                material.albedoTexture = this._texture_manager.get(this._custom_base_color_texture);
            }
            if (this._custom_emissive_color !== null) {
                material.emissiveColor.set(Math.pow(this._custom_emissive_color.r, 2.2), Math.pow(this._custom_emissive_color.g, 2.2), Math.pow(this._custom_emissive_color.b, 2.2));
            }
            if (this._custom_emissive_intensity !== -1) {
                material.emissiveIntensity = this._custom_emissive_intensity;
            }
            if (this._custom_normal_texture !== null) {
                material.bumpTexture = this._texture_manager.get(this._custom_normal_texture);
            }
            if (this._custom_occlusion_roughness_metallic_texture !== null) {
                material.metallicTexture = this._texture_manager.get(this._custom_occlusion_roughness_metallic_texture);
            }
            if (this._custom_lightmap_texture !== null) {
                material.lightmapTexture = this._texture_manager.get(this._custom_lightmap_texture);
            }
        }
        this.set_dirty();
    }
    get roughness() {
        return this._custom_roughness !== null ? this._custom_roughness : this._original_roughness;
    }
    set roughness(v) {
        this._custom_roughness = v;
        if (this._material) {
            this._material.roughness = v;
        }
        this.set_dirty();
    }
    reset_roughness() {
        this._custom_roughness = null;
        if (this._material) {
            this._material.roughness = this._original_roughness;
        }
        this.set_dirty();
    }
    get metallic() {
        return this._custom_metallic !== null ? this._custom_metallic : this._original_metallic;
    }
    set metallic(v) {
        this._custom_metallic = v;
        if (this._material) {
            this._material.metallic = v;
        }
        this.set_dirty();
    }
    reset_metallic() {
        this._custom_metallic = null;
        if (this._material) {
            this._material.metallic = this._original_metallic;
        }
        this.set_dirty();
    }
    get base_color() {
        return this._custom_base_color !== null ? this._custom_base_color : this._original_base_color;
    }
    set base_color(v) {
        this._custom_base_color = v;
        if (this._material) {
            this._material.albedoColor.set(Math.pow(v.r, 2.2), Math.pow(v.g, 2.2), Math.pow(v.b, 2.2));
        }
        this.set_dirty();
    }
    get emissive_color() {
        return this._custom_emissive_color !== null ? this._custom_emissive_color : this._original_emissive_color;
    }
    set emissive_color(v) {
        this._custom_emissive_color = v;
        if (this._material) {
            this._material.emissiveColor.set(Math.pow(v.r, 2.2), Math.pow(v.g, 2.2), Math.pow(v.b, 2.2));
        }
        this.set_dirty();
    }
    reset_emissive_color() {
        this._custom_emissive_color = null;
        if (this._material) {
            this._material.emissiveColor.set(Math.pow(this._original_emissive_color.r, 2.2), Math.pow(this._original_emissive_color.g, 2.2), Math.pow(this._original_emissive_color.b, 2.2));
        }
        this.set_dirty();
    }
    set emissive_intensity(v) {
        this._custom_emissive_intensity = v;
        if (this._material) {
            this._material.emissiveIntensity = v;
        }
        this.set_dirty();
    }
    reset_emissive_intensity(v) {
        this._custom_emissive_intensity = -1;
        if (this._material) {
            this._material.emissiveIntensity = this._original_emissive_intensity;
        }
        this.set_dirty();
    }
    reset_base_color() {
        this._custom_base_color = null;
        if (this._material) {
            this._material.albedoColor.set(Math.pow(this._original_base_color.r, 2.2), Math.pow(this._original_base_color.g, 2.2), Math.pow(this._original_base_color.b, 2.2));
        }
        this.set_dirty();
    }
    get base_color_texture() {
        return this._custom_base_color_texture !== null ? this._custom_base_color_texture : this._original_base_color_texture;
    }
    set base_color_texture(v) {
        this._custom_base_color_texture = v;
        if (this._material) {
            this.set_texture('albedoTexture', v);
        }
    }
    reset_base_color_texture() {
        this._custom_base_color_texture = null;
        if (this.material) {
            this.set_texture('albedoTexture', this._original_base_color_texture);
        }
    }
    get normal_texture() {
        return this._custom_normal_texture !== null ? this._custom_normal_texture : this._original_normal_texture;
    }
    set normal_texture(v) {
        this._custom_normal_texture = v;
        if (this.material) {
            this.set_texture('bumpTexture', v);
        }
    }
    reset_normal_texture() {
        this._custom_normal_texture = null;
        if (this.material) {
            this.set_texture('bumpTexture', this._original_normal_texture);
        }
    }
    get occlusion_roughness_metallic_texture() {
        return this._custom_occlusion_roughness_metallic_texture !== null
            ? this._custom_occlusion_roughness_metallic_texture
            : this._original_occlusion_roughness_metallic_texture;
    }
    set occlusion_roughness_metallic_texture(v) {
        this._custom_occlusion_roughness_metallic_texture = v;
        if (this.material) {
            this.set_texture('metallicTexture', v);
        }
    }
    reset_occlusion_roughness_metallic_texture() {
        this._custom_occlusion_roughness_metallic_texture = null;
        if (this.material) {
            this.set_texture('metallicTexture', this._original_occlusion_roughness_metallic_texture);
        }
    }
    get lightmap_texture() {
        return this._custom_lightmap_texture !== null ? this._custom_lightmap_texture : this._original_lightmap_texture;
    }
    set lightmap_texture(v) {
        this._custom_lightmap_texture = v;
        if (this.material) {
            this.set_texture('lightmapTexture', v);
        }
    }
    get unlit() {
        return this.material.unlit;
    }
    set unlit(unlit) {
        this.material.unlit = unlit;
    }
    set_opacity(alpha) {
        if (this.material) {
            if (alpha < 1.0) {
                this.material.alphaMode = BABYLON_Constants.ALPHA_COMBINE;
                if (this.material.transparencyMode === ViewerMaterial.PBRMATERIAL_ALPHATEST) {
                    this.material.transparencyMode = ViewerMaterial.PBRMATERIAL_ALPHATESTANDBLEND;
                }
                else {
                    this.material.transparencyMode = ViewerMaterial.PBRMATERIAL_ALPHABLEND;
                }
            }
            else { // I see no case ever where a user would want to make a previously transparent material solid again, but might as well put it here so we can do that if we choose to
                this.material.alphaMode = BABYLON_Constants.ALPHA_DISABLE;
                if (this.material.transparencyMode === ViewerMaterial.PBRMATERIAL_ALPHATESTANDBLEND) {
                    this.material.transparencyMode = ViewerMaterial.PBRMATERIAL_ALPHATEST;
                }
                else {
                    this.material.transparencyMode = ViewerMaterial.PBRMATERIAL_OPAQUE;
                }
            }
            this.material.alpha = alpha;
        }
    }
    reset_lightmap_texture() {
        this._custom_lightmap_texture = null;
        if (this.material) {
            this.set_texture('lightmapTexture', this._original_lightmap_texture);
        }
    }
    set_texture(texture_property, id) {
        if (this.material) {
            if (id !== null) {
                this.material[texture_property] = this._texture_manager.get(id);
            }
            else {
                this.material[texture_property] = null;
            }
            this.set_dirty();
        }
    }
    init_texture(texture) {
        const id = this._texture_manager.find_id(texture);
        if (id >= 0) {
            return id;
        }
        return this._texture_manager.add(texture);
    }
    set_dirty() {
        if (this.material) {
            this.freezing_manager.set_material_dirty(this.material);
        }
        if (this.multi_material) {
            for (const submaterial of this.multi_material.subMaterials) {
                if (submaterial) {
                    this.freezing_manager.set_material_dirty(submaterial);
                }
            }
        }
    }
    dispose() {
        this.ready.then(() => {
            if (this._material) {
                this._material.dispose();
                this._material = null;
            }
            if (this._multi_material) {
                this._multi_material.dispose();
                this._multi_material = null;
            }
        }, () => {
            console.warn('Material is not ready to be disposed.');
        });
    }
}
//# sourceMappingURL=Material.js.map